import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import SyncedGameFrame from '../../components/SyncedGameFrame';
import { Link } from 'react-router-dom';
import NavBar from '../../components/NavBar';
import Breadcrumbs from '../../components/Breadcrumbs';
import StoreCTA from '../../components/StoreCTA';
import '../../styles/gameInfo.css';

// Games that are also inside the Daily Logic Puzzles app.
const IN_DLP = new Set(['sudoku', 'mine-finder', 'tent-logic']);

const CATEGORY_NAMES = {
  '/logic-games': 'Logic Games',
  '/word-games': 'Word Games',
  '/card-games': 'Card Games',
};

// Click-to-play facade: zero network cost until the visitor presses Play,
// then the real game iframe loads inline on this (indexed) page.
function InlinePlayer({ name, playPath, embedSrc, started, onStart }) {
  return (
    <div className="gi-player" id="play">
      {started ? (
        <>
          <div className="gi-player__frame">
            <SyncedGameFrame src={embedSrc} title={`Play ${name} online`} gameName={name} allow="fullscreen" allowFullScreen />
          </div>
          <p className="gi-player__foot">
            <Link to={playPath}>Open the full-screen player →</Link>
          </p>
        </>
      ) : (
        <div className="gi-player__start">
          <h2>Play {name} online, free</h2>
          <p>No signup, no download. It plays right here in your browser.</p>
          <button type="button" className="button" onClick={onStart}>
            ▶ Play {name} now
          </button>
          <p>
            <Link to={playPath}>Or open the full-screen player →</Link>
          </p>
        </div>
      )}
    </div>
  );
}

function GameContentPage({ gameData }) {
  const {
    slug,
    playPath,
    printPath,
    categoryPath,
    name,
    subtitle,
    description,
    keywords,
    ogImage,
    intro,
    howToPlay,
    strategyTips,
    faq,
    ctaText,
    history,
    similarGames,
    commonMistakes,
    app,
    appHeadline,
  } = gameData;

  const [started, setStarted] = useState(false);
  const startPlaying = () => {
    setStarted(true);
    document.getElementById('play')?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  };

  const dlpCta = (
    <StoreCTA
      app="dlp"
      placement="game-page"
      page={`/games/${slug}`}
      headline={IN_DLP.has(slug)
        ? `Play a new ${name} puzzle every day in the free Daily Logic Puzzles app`
        : `Enjoying ${name}? Get eight new logic puzzles every day in our free app`}
    />
  );

  const canonicalUrl = `https://logicpuzzles.ca/games/${slug}`;
  const categoryName = CATEGORY_NAMES[categoryPath] || 'Logic Games';
  // The /play/ route slug matches the public game-build directory for all data-driven games
  const embedSrc = `${playPath.replace('/play/', '/')}/index.html`;
  const iconSrc = `${playPath.replace('/play/', '/')}/icons/Icon-192.png`;
  const absoluteImage = ogImage ? `https://logicpuzzles.ca${ogImage}` : 'https://logicpuzzles.ca/og-image.png';

  const videoGameSchema = {
    '@context': 'https://schema.org',
    '@type': 'VideoGame',
    'name': name,
    'description': description,
    'url': canonicalUrl,
    'image': absoluteImage,
    'genre': `${categoryName.replace(/s$/, '')} Puzzle`,
    'gamePlatform': 'Web Browser',
    'applicationCategory': 'Game',
    'operatingSystem': 'Any (browser-based)',
    'playMode': 'SinglePlayer',
    'inLanguage': 'en',
    'isAccessibleForFree': true,
    'offers': {
      '@type': 'Offer',
      'price': '0',
      'priceCurrency': 'CAD',
      'availability': 'https://schema.org/InStock',
    },
    'publisher': { '@id': 'https://logicpuzzles.ca/#organization' },
    'potentialAction': {
      '@type': 'PlayAction',
      'target': `https://logicpuzzles.ca${playPath}`,
    },
  };

  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    'mainEntity': faq.map(item => ({
      '@type': 'Question',
      'name': item.question,
      'acceptedAnswer': {
        '@type': 'Answer',
        'text': item.answer,
      },
    })),
  };

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    'itemListElement': [
      { '@type': 'ListItem', 'position': 1, 'name': 'Home', 'item': 'https://logicpuzzles.ca' },
      { '@type': 'ListItem', 'position': 2, 'name': 'Logic Games', 'item': `https://logicpuzzles.ca${categoryPath || '/logic-games'}` },
      { '@type': 'ListItem', 'position': 3, 'name': name, 'item': canonicalUrl },
    ],
  };

  return (
    <div>
      <Helmet>
        <title>{gameData.seoTitle || `${name} - Play Free Online, Rules & Tips | Logic Puzzles CA`}</title>
        <meta name="description" content={description} />
        <meta name="keywords" content={keywords} />
        <meta property="og:title" content={`${name} - Play Free Online`} />
        <meta property="og:description" content={description} />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={canonicalUrl} />
        {ogImage && <meta property="og:image" content={`https://logicpuzzles.ca${ogImage}`} />}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={`${name} - Play Free Online`} />
        <meta name="twitter:description" content={description} />
        {ogImage && <meta name="twitter:image" content={`https://logicpuzzles.ca${ogImage}`} />}
        <link rel="canonical" href={canonicalUrl} />
        <script type="application/ld+json">{JSON.stringify(videoGameSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(breadcrumbSchema)}</script>
      </Helmet>

      <NavBar />
      <Breadcrumbs
        categoryName={categoryName}
        categoryPath={categoryPath || '/logic-games'}
        currentLabel={name}
      />

      <header className="gi-hero">
        <div className="container gi-hero__inner">
          <div>
            <p className="eyebrow">{categoryName.replace(/s$/, '')} · Free online</p>
            <h1>Play {name} online</h1>
            <p className="gi-hero__lead">{subtitle}</p>
            <div className="gi-hero__actions">
              <button type="button" className="button" onClick={startPlaying}>
                ▶ Play now
              </button>
              <a href="#how-to-play" className="button secondary">How to play</a>
              {printPath && (
                <Link to={printPath} className="button secondary">Printable puzzles</Link>
              )}
              {app && (
                <StoreCTA app={app} variant="badges" placement="game-hero" page={`/games/${slug}`} />
              )}
            </div>
          </div>
          <div className="gi-hero__art" aria-hidden="true">
            <img src={iconSrc} alt="" width="168" height="168" />
          </div>
        </div>
      </header>

      <div className="gi-main">
        {/* Play inline without leaving the page */}
        <InlinePlayer name={name} playPath={playPath} embedSrc={embedSrc} started={started} onStart={() => setStarted(true)} />

        {/* Games with their own app promote it here; the Daily Logic Puzzles
            card then moves down the page (and still serves iPhone visitors). */}
        {app ? (
          <StoreCTA app={app} placement="game-page" page={`/games/${slug}`} headline={appHeadline} />
        ) : dlpCta}

        <section className="gi-section">
          <p className="gi-lead" dangerouslySetInnerHTML={{ __html: intro }} />
        </section>

        <section className="gi-section" id="how-to-play">
          <h2>How to play {name}</h2>
          <div className="gi-grid">
            {howToPlay.map((item, i) => (
              <div key={i} className="gi-card">
                <span className="gi-card__num">{String(i + 1).padStart(2, '0')}</span>
                <h3>{item.title}</h3>
                <p>{item.text}</p>
              </div>
            ))}
          </div>
        </section>

        <section className="gi-section">
          <h2>Strategy &amp; solving tips</h2>
          <p dangerouslySetInnerHTML={{ __html: strategyTips.intro }} />
          <ul className="gi-list">
            {strategyTips.tips.map((tip, i) => (
              <li key={i}>{tip}</li>
            ))}
          </ul>
        </section>

        {commonMistakes && commonMistakes.length > 0 && (
          <section className="gi-section">
            <h2>Common mistakes to avoid</h2>
            <ul className="gi-list gi-list--warn">
              {commonMistakes.map((mistake, i) => (
                <li key={i}>{mistake}</li>
              ))}
            </ul>
          </section>
        )}

        {history && (
          <section className="gi-section">
            <h2>History &amp; origins</h2>
            <p dangerouslySetInnerHTML={{ __html: history }} />
          </section>
        )}

        <section className="gi-section">
          <h2>{name} FAQ</h2>
          <div className="gi-faq">
            {faq.map((item, i) => (
              <details key={i} open={i === 0}>
                <summary>{item.question}</summary>
                <p>{item.answer}</p>
              </details>
            ))}
          </div>
        </section>

        {similarGames && similarGames.length > 0 && (
          <section className="gi-section">
            <h2>Similar puzzles you might enjoy</h2>
            <div className="gi-grid">
              {similarGames.map((game, i) => (
                <Link key={i} to={game.path} className="gi-card">
                  <h3>{game.name} →</h3>
                  <p>{game.description}</p>
                </Link>
              ))}
            </div>
          </section>
        )}

        {app && dlpCta}

        <section className="gi-cta">
          <h2>Ready to play {name}?</h2>
          <p dangerouslySetInnerHTML={{ __html: ctaText }} />
          <div className="gi-cta__actions">
            <Link to={playPath} className="button">Play {name} now</Link>
            {printPath && (
              <Link to={printPath} className="button secondary">Print puzzles</Link>
            )}
            <Link to={categoryPath || '/logic-games'} className="button secondary">More puzzles</Link>
          </div>
        </section>
      </div>
    </div>
  );
}

export default GameContentPage;
